import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL, getApiUrl } from '../../config';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { useRecipeUndo } from '../../contexts/RecipeUndoContext';
import { sanitizeRecipeName } from '../../utils/validation';
import AlertDialog from '../Common/AlertDialog';

// Create a shared recipe service for reuse
export const recipeService = {
  // Delete a recipe and return delete info
  deleteRecipe: async (id: number, token: string | null): Promise<{deletedAt: string, recipeId: string}> => {
    if (!token) throw new Error('Authentication token is required');
    
    const response = await fetch(getApiUrl(`recipes/${id}`), {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to delete recipe');
    }
    
    return response.json();
  }
};

interface Recipe {
  id: number;
  name: string;
  content: string;
  is_active: boolean;
  planSuspendedAt?: string | null;
  engine_error: string | null;
  updatedAt: string;
  createdAt?: string;
}

const recipeStatusRank = (recipe: Recipe): number => {
  if (recipe.is_active && !recipe.planSuspendedAt) return 0;
  if (recipe.planSuspendedAt) return 1;
  if (recipe.engine_error?.trim()) return 2;
  return 3;
};

const recipeTimestamp = (recipe: Recipe): number => {
  const timestamp = Date.parse(recipe.updatedAt || recipe.createdAt || '');
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const sortRecipes = (recipes: Recipe[]): Recipe[] => [...recipes].sort((left, right) => {
  const statusDifference = recipeStatusRank(left) - recipeStatusRank(right);
  if (statusDifference !== 0) return statusDifference;

  const timestampDifference = recipeTimestamp(right) - recipeTimestamp(left);
  if (timestampDifference !== 0) return timestampDifference;

  return right.id - left.id;
});

export type RecipeSidebarUpdate = Partial<Recipe> & Pick<Recipe, 'id'>;

interface RecipeSidebarProps {
  selectedRecipe?: string;
  refreshKey?: number;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export interface RecipeSidebarRef {
  refreshRecipes: (showLoading?: boolean) => void;
  updateRecipe: (recipe: RecipeSidebarUpdate) => void;
  handleDeletedRecipe: (recipeId: number) => void;
}

const RecipeSidebar = forwardRef<RecipeSidebarRef, RecipeSidebarProps>((props, ref) => {
  const { selectedRecipe, refreshKey, mobileOpen = false, onMobileClose } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { token, logout, currentUser, impersonation, exitImpersonation } = useAuth();
  const { hasUnsavedChanges } = useNavigation();
  const { showRecipeUndo } = useRecipeUndo();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<string | null>(null);
  const [isRenaming, setIsRenaming] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [localSelectedRecipe, setLocalSelectedRecipe] = useState<string | undefined>(selectedRecipe);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [appVersion, setAppVersion] = useState<string | null>(null);
  const [appDeployedAt, setAppDeployedAt] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [alertDialog, setAlertDialog] = useState<{ title: string; message: string } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const mobileCloseRef = useRef<HTMLButtonElement>(null);
  const asideRef = useRef<HTMLElement>(null);
  const showExpanded = isExpanded || mobileOpen;

  useEffect(() => {
    if (!mobileOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    mobileCloseRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onMobileClose?.();
        return;
      }
      if (event.key !== 'Tab' || !asideRef.current) return;
      const focusable = Array.from(asideRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter(element => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileOpen, onMobileClose]);

  useEffect(() => {
    onMobileClose?.();
  // Close the drawer after navigation. The callback is intentionally excluded
  // because parents normally provide it inline.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  
  // Update local selection state when prop changes
  useEffect(() => {
    setLocalSelectedRecipe(selectedRecipe);
  }, [selectedRecipe]);

  useEffect(() => {
    setAvatarFailed(false);
  }, [currentUser?.googleAvatarUrl]);

  useEffect(() => {
    let stopped = false;
    fetch(getApiUrl('version'), { cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(response => response.ok ? response.json() : null)
      .then(body => {
        const version = typeof body?.version === 'string' ? body.version.trim() : '';
        const deployedAt = typeof body?.deployedAt === 'string' ? body.deployedAt.trim() : '';
        if (!stopped && /^\d+\.\d+\.\d+$/.test(version)) {
          setAppVersion(version);
          if (!Number.isNaN(Date.parse(deployedAt))) setAppDeployedAt(deployedAt);
        }
      })
      .catch(() => { /* Version is diagnostic metadata, not a navigation dependency. */ });
    return () => { stopped = true; };
  }, []);

  const deployedMinutes = currentUser?.role === 'admin' && appDeployedAt
    ? Math.max(0, Math.floor((Date.now() - Date.parse(appDeployedAt)) / 60000))
    : null;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
        setMenuPosition(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchRecipes = async (showLoading = true) => {
    if (showLoading) setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/recipes`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch recipes');
      }

      const data = await response.json() as Recipe[];
      setRecipes(sortRecipes(data));
    } catch (err) {
      console.error('Error fetching recipes:', err);
    } finally {
      if (showLoading) setIsLoading(false);
    }
  };

  const handleDeletedRecipe = (recipeId: number) => {
    const remainingRecipes = recipes.filter(recipe => recipe.id !== recipeId);
    const currentRecipeId = Number(location.pathname.match(/^\/recipes\/(\d+)\/edit$/)?.[1]);

    setRecipes(remainingRecipes);

    if (currentRecipeId === recipeId) {
      setLocalSelectedRecipe(undefined);
      navigate('/recipes/new', { replace: true });
    }
  };

  const updateRecipe = (updatedRecipe: RecipeSidebarUpdate) => {
    setRecipes(currentRecipes => sortRecipes(currentRecipes.map(recipe =>
      recipe.id === updatedRecipe.id ? { ...recipe, ...updatedRecipe } : recipe
    )));
  };
  
  // Expose the fetchRecipes method via the ref
  useImperativeHandle(ref, () => ({
    refreshRecipes: fetchRecipes,
    updateRecipe,
    handleDeletedRecipe
  }));

  // Fetch recipes when token or refreshKey changes
  useEffect(() => {
    fetchRecipes();
    const statusRefresh = window.setInterval(() => fetchRecipes(false), 10_000);

    return () => window.clearInterval(statusRefresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, refreshKey]);

  useEffect(() => {
    const refreshRestoredRecipe = () => void fetchRecipes(false);
    document.addEventListener('recipeRestored', refreshRestoredRecipe);
    return () => document.removeEventListener('recipeRestored', refreshRestoredRecipe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleNavigation = (path: string) => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate(path);
      }
    } else {
      navigate(path);
    }
    setIsMenuOpen(false);
    onMobileClose?.();
  };

  const handleLogout = () => {
    if (impersonation) {
      void exitImpersonation();
      setIsMenuOpen(false);
      onMobileClose?.();
      return;
    }
    logout();
    navigate('/login');
    setIsMenuOpen(false);
    onMobileClose?.();
  };
  
  const handleRename = async (recipe: Recipe) => {
    if (!recipe.id || !newName || recipe.name === newName) {
      setIsRenaming(null);
      return;
    }
    
    // Sanitize the new name before sending to the backend
    const sanitizedNewName = sanitizeRecipeName(newName);
    
    // If sanitized name is different from input, update the state
    if (sanitizedNewName !== newName) {
      setNewName(sanitizedNewName);
    }
    
    try {
      // Call the backend API to rename the recipe
      const response = await fetch(getApiUrl(`recipes/${recipe.id}/rename`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ newName: sanitizedNewName })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to rename recipe');
      }
      
      // Refresh the recipe list
      await fetchRecipes();
      
      // If this is the selected recipe, redirect to the new URL path with ID
      if (recipe.name === selectedRecipe) {
        navigate(`/recipes/${recipe.id}/edit`);
      }
    } catch (err) {
      console.error('Error renaming recipe:', err);
      setAlertDialog({ title: 'Could not rename recipe', message: 'Please try again.' });
    } finally {
      setIsRenaming(null);
      setMenuPosition(null);
    }
  };
  
  const handleDelete = async (recipe: Recipe) => {
    if (!recipe || !recipe.id || !token) return;

    const wasSelected = Number(location.pathname.match(/^\/recipes\/(\d+)\/edit$/)?.[1]) === recipe.id;
    
    try {
      await recipeService.deleteRecipe(recipe.id, token);
      handleDeletedRecipe(recipe.id);
      showRecipeUndo({
        recipeId: recipe.id,
        recipeName: recipe.name,
        reopenOnUndo: wasSelected,
      });
      
    } catch (err) {
      console.error('Error deleting recipe:', err);
      setAlertDialog({ title: 'Could not delete recipe', message: 'Please try again.' });
    } finally {
      setMenuPosition(null);
    }
  };

  return (
    <>
    {alertDialog && (
      <AlertDialog
        title={alertDialog.title}
        message={alertDialog.message}
        onClose={() => setAlertDialog(null)}
      />
    )}
    <button
      type="button"
      aria-label="Close Kitchen navigation"
      onClick={onMobileClose}
      className={`fixed inset-0 z-40 bg-black/35 transition-opacity lg:hidden ${mobileOpen ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
    />
    <aside
      ref={asideRef}
      className={`fixed inset-y-0 left-0 z-50 h-full w-80 max-w-[85vw] flex-shrink-0 transform transition-transform duration-200 ease-out lg:relative lg:z-30 lg:w-16 lg:min-w-[4rem] lg:max-w-[4rem] lg:translate-x-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => {
        setIsExpanded(false);
        setIsMenuOpen(false);
        setMenuPosition(null);
        setIsRenaming(null);
      }}
    >
      <div className={`${showExpanded ? 'w-full shadow-xl lg:w-64' : 'w-16'} absolute inset-y-0 left-0 flex flex-col overflow-hidden border-r border-gray-200/80 bg-gray-50 pt-[env(safe-area-inset-top)] text-gray-800 transition-[width,box-shadow] duration-200 ease-out lg:pt-0`}>
      <button
        type="button"
        onClick={() => handleNavigation('/')}
        className={`mx-2 mt-2 flex h-11 shrink-0 items-center rounded-lg text-left font-semibold tracking-tight text-gray-950 transition-colors hover:bg-gray-200/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 ${showExpanded ? 'gap-3 px-3' : 'justify-center px-0'}`}
        aria-label="Kitchen home"
        title={showExpanded ? undefined : 'Kitchen'}
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gray-900 text-sm font-bold text-white" aria-hidden="true">K</span>
        {showExpanded && <span className="whitespace-nowrap text-base">Kitchen</span>}
      </button>
      <button
        ref={mobileCloseRef}
        type="button"
        onClick={onMobileClose}
        className="absolute right-2 top-2 inline-flex h-11 w-11 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 lg:hidden"
        aria-label="Close navigation"
      >
        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" strokeLinecap="round" /></svg>
      </button>
      
      {/* Primary navigation */}
      <nav className="space-y-1 px-2 pb-3 pt-2" aria-label="Recipe navigation">
        <button
          type="button"
          onClick={() => handleNavigation('/recipes/new')}
          className={`group flex h-11 w-full shrink-0 items-center rounded-lg text-left text-[0.9625rem] font-medium text-gray-800 transition-colors hover:bg-gray-200/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 ${showExpanded ? 'gap-3 px-3' : 'justify-center px-0'}`}
          aria-label="New recipe"
          title={showExpanded ? undefined : 'New recipe'}
        >
          <svg className="h-5 w-5 flex-shrink-0 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 7.125V18A2.625 2.625 0 0116.875 20.625H5.625A2.625 2.625 0 013 18V6.75a2.625 2.625 0 012.625-2.625H16.5" />
          </svg>
          {showExpanded && <span className="whitespace-nowrap">New recipe</span>}
        </button>
        <button
          type="button"
          onClick={() => handleNavigation('/gallery')}
          className={`group flex h-11 w-full shrink-0 items-center rounded-lg text-left text-[0.9625rem] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 ${showExpanded ? 'gap-3 px-3' : 'justify-center px-0'} ${location.pathname === '/gallery' ? 'bg-gray-200 text-gray-950' : 'text-gray-800 hover:bg-gray-200/80'}`}
          aria-label="Gallery"
          title={showExpanded ? undefined : 'Gallery'}
        >
          <svg className="h-5 w-5 flex-shrink-0 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
            <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h5A1.5 1.5 0 0 1 12 5.5v5a1.5 1.5 0 0 1-1.5 1.5h-5A1.5 1.5 0 0 1 4 10.5v-5ZM14 5.5A1.5 1.5 0 0 1 15.5 4h3A1.5 1.5 0 0 1 20 5.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 14 8.5v-3ZM14 13.5a1.5 1.5 0 0 1 1.5-1.5h3a1.5 1.5 0 0 1 1.5 1.5v5a1.5 1.5 0 0 1-1.5 1.5h-3a1.5 1.5 0 0 1-1.5-1.5v-5ZM4 15.5A1.5 1.5 0 0 1 5.5 14h5a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-5A1.5 1.5 0 0 1 4 18.5v-3Z" strokeLinejoin="round" />
          </svg>
          {showExpanded && <span className="whitespace-nowrap">Gallery</span>}
        </button>
        <button
          type="button"
          onClick={() => handleNavigation('/search')}
          title={showExpanded ? undefined : 'Search'}
          className={`group flex h-11 w-full shrink-0 items-center rounded-lg text-left text-[0.9625rem] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 ${showExpanded ? 'gap-3 px-3' : 'justify-center px-0'} ${location.pathname === '/search' ? 'bg-gray-200 text-gray-950' : 'text-gray-800 hover:bg-gray-200/80'}`}
          aria-label="Search"
        >
          <svg className="h-5 w-5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35m2.1-5.4a7.5 7.5 0 11-15 0 7.5 7.5 0 0115 0z" />
          </svg>
          {showExpanded && <span className="whitespace-nowrap">Search</span>}
        </button>
        <button
          type="button"
          onClick={() => handleNavigation('/vault')}
          className={`group flex h-11 w-full shrink-0 items-center rounded-lg text-left text-[0.9625rem] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 ${showExpanded ? 'gap-3 px-3' : 'justify-center px-0'} ${location.pathname === '/vault' ? 'bg-gray-200 text-gray-950' : 'text-gray-800 hover:bg-gray-200/80'}`}
          aria-label="Vault"
          title={showExpanded ? undefined : 'Vault'}
        >
          <svg className="h-5 w-5 flex-shrink-0 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-7a2 2 0 00-2-2H6a2 2 0 00-2 2v7a2 2 0 002 2zm10-11V7a4 4 0 00-8 0v3h8z" />
          </svg>
          {showExpanded && <span className="whitespace-nowrap">Vault</span>}
        </button>
        {currentUser?.role === 'admin' && (
          <button
            type="button"
            onClick={() => handleNavigation('/admin')}
            className={`group flex h-11 w-full shrink-0 items-center rounded-lg text-left text-[0.9625rem] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 ${showExpanded ? 'gap-3 px-3' : 'justify-center px-0'} ${location.pathname.startsWith('/admin') ? 'bg-gray-200 text-gray-950' : 'text-gray-800 hover:bg-gray-200/80'}`}
            aria-label="Admin"
            title={showExpanded ? undefined : 'Admin'}
          >
            <svg className="h-5 w-5 flex-shrink-0 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            {showExpanded && <span className="whitespace-nowrap">Admin</span>}
          </button>
        )}
      </nav>
      
      {/* Recipe list - scrollable middle section */}
      <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-3">
        <div
          className={`h-8 whitespace-nowrap px-3 pt-1 text-[0.825rem] font-semibold text-gray-500 ${showExpanded ? '' : 'invisible'}`}
          aria-hidden={!showExpanded}
        >
          Your recipes
        </div>
        <div className="space-y-1">
          {isLoading ? (
            <div className={`${showExpanded ? 'px-3 text-left' : 'px-0 text-center'} py-4 text-[0.9625rem] text-gray-400`}>{showExpanded ? 'Loading recipes…' : '…'}</div>
          ) : (
            recipes.map((recipe) => (
              <div 
                key={recipe.name}
                className={`group relative flex h-11 items-center rounded-lg text-[0.9625rem] transition-colors ${showExpanded ? 'px-3' : 'px-1 text-center'} ${
                  recipe.name === localSelectedRecipe 
                    ? 'bg-gray-200 text-gray-950'
                    : 'hover:bg-gray-200/70'
                } cursor-pointer`}
                onClick={() => {
                  setLocalSelectedRecipe(recipe.name);
                  handleNavigation(`/recipes/${recipe.id}/edit`);
                }}
                title={showExpanded ? undefined : recipe.name}
                aria-label={showExpanded ? undefined : `Recipe ${recipe.id}: ${recipe.name}`}
              >
                <div className="flex w-full items-center justify-between">
                  {showExpanded && isRenaming === recipe.name ? (
                    <div className="flex items-center w-full">
                      <input
                        type="text"
                        className="min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-2 py-1 mr-2 focus:outline-none focus:ring-2 focus:ring-gray-400"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleRename(recipe);
                          } else if (e.key === 'Escape') {
                            setIsRenaming(null);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRename(recipe);
                        }}
                        className="rounded p-1 text-gray-600 hover:bg-gray-300 hover:text-gray-900 mr-1"
                        title="Save"
                      >
                        ✓
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsRenaming(null);
                        }}
                        className="rounded p-1 text-gray-500 hover:bg-red-100 hover:text-red-700"
                        title="Cancel"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    showExpanded ? <>
                      <div className="flex min-w-0 flex-1 items-center">
                        {/* Status indicator */}
                        <span 
                          className={`w-1.5 h-1.5 rounded-full mr-3 flex-shrink-0 ${
                            recipe.planSuspendedAt ? 'bg-amber-500' : recipe.is_active ? 'bg-emerald-500' : 'bg-gray-300'
                          }`}
                          title={recipe.planSuspendedAt ? 'Paused by plan' : recipe.is_active ? 'Active' : 'Inactive'}
                        ></span>
                        
                        <div 
                          className="truncate font-normal"
                        >
                          {recipe.name}
                        </div>
                      </div>
                      <button
                        className={`-mr-1 ml-1 rounded-md p-2 transition-opacity hover:bg-gray-300 ${menuPosition === recipe.name ? 'opacity-100' : 'opacity-100 lg:opacity-0 lg:group-hover:opacity-100'}`}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent parent onClick from firing
                          setMenuPosition(menuPosition === recipe.name ? null : recipe.name);
                        }}
                        title="Options"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500 hover:text-gray-700" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                        </svg>
                      </button>
                    </> : (
                      <span className={`mx-auto inline-flex h-6 min-w-[1.875rem] items-center justify-center rounded-md border px-1 font-mono text-xs font-semibold ${
                        recipe.planSuspendedAt
                          ? 'border-amber-400 text-amber-700'
                          : recipe.is_active
                          ? 'border-emerald-400 text-emerald-700'
                          : recipe.engine_error
                            ? 'border-red-300 text-red-600'
                            : 'border-gray-300 text-gray-600'
                      }`}>
                        {recipe.id}
                      </span>
                    )
                  )}
                </div>
                
                {/* Dropdown menu */}
                {showExpanded && menuPosition === recipe.name && (
                  <div 
                    ref={recipe.name === menuPosition ? menuRef : undefined}
                    className="relative z-10"
                  >
                    <div className="absolute right-0 mt-2 w-36 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-xl">
                      <ul>
                        <li className="border-b border-gray-100">
                          <button
                            className="w-full text-left px-3 py-2 text-[0.9625rem] text-gray-700 hover:bg-gray-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsRenaming(recipe.name);
                              setNewName(recipe.name);
                              setMenuPosition(null);
                            }}
                          >
                            Rename
                          </button>
                        </li>
                        <li>
                          <button
                            className="w-full text-left px-3 py-2 text-[0.9625rem] text-red-600 hover:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(recipe);
                            }}
                          >
                            Delete
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* User account section at bottom */}
      <div className="border-t border-gray-200/80 p-2 relative" ref={menuRef}>
        <div 
          className="flex items-center justify-between rounded-lg px-2 py-2 cursor-pointer hover:bg-gray-200/80 transition-colors"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <div className={`flex min-w-0 items-center ${showExpanded ? '' : 'w-full justify-center'}`}>
            {currentUser?.googleAvatarUrl && !avatarFailed ? (
              <img
                src={currentUser.googleAvatarUrl}
                alt=""
                referrerPolicy="no-referrer"
                onError={() => setAvatarFailed(true)}
                className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-800 text-white">
                <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            )}
            {showExpanded && (
              <span
                className="ml-2.5 min-w-0 truncate text-[0.9625rem] font-medium"
                title={currentUser?.email}
              >
                {currentUser?.email || 'Account'}
              </span>
            )}
          </div>
          {showExpanded && <svg
            className={`ml-2 h-4 w-4 flex-shrink-0 text-gray-500 transform transition-transform ${isMenuOpen ? 'rotate-180' : ''}`}
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 20 20" 
            fill="currentColor"
          >
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>}
        </div>
        
        {/* Dropdown menu */}
        {showExpanded && isMenuOpen && (
          <div className="mb-1 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl z-10">
            <div className="py-1" role="menu" aria-orientation="vertical">
              <button
                onClick={() => handleNavigation('/account')}
                className="block w-full text-left px-3 py-2.5 text-[0.9625rem] text-gray-700 hover:bg-gray-100 transition-colors"
                role="menuitem"
              >
                <div className="flex items-center">
                  <svg className="mr-3 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Settings
                </div>
              </button>
              <button
                onClick={handleLogout}
                className="block w-full text-left px-3 py-2.5 text-[0.9625rem] text-red-600 hover:bg-red-50 transition-colors"
                role="menuitem"
              >
                <div className="flex items-center">
                  <svg className="mr-3 h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Logout
                </div>
              </button>
            </div>
            {appVersion && (
              <div
                className="border-t border-gray-100 px-3 py-2 text-center font-mono text-[0.6875rem] text-gray-400"
                title={`Version ${appVersion}${deployedMinutes !== null ? `, deployed ${deployedMinutes} minutes ago` : ''}`}
              >
                v{appVersion}
                {deployedMinutes !== null && ` · deployed ${deployedMinutes}m ago`}
              </div>
            )}
          </div>
        )}
      </div>
      </div>
    </aside>
    </>
  );
});

export default RecipeSidebar;
